'use client';

import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useGuestCart } from './useGuestCart';
import { useAuthCart } from './useAuthCart';
import type { CartAddon } from './addons';
import { addonsKey } from './addons';
import { cartItemKey } from './cartItemKey';
import { resolveCartItemPrice } from './resolveCartPrice';
import { availableUnits, CartStockError } from './stock';
import { DEFAULT_CURRENCY_CODE } from '@/lib/currency/CurrencyContext';
import { trackAddToCart } from '@/lib/tracking/events';
import type { PriceTierDTO } from '@/lib/storefront/products/service';
export type CartItem = {
  product_id: string;
  variant_id?: string | null;
  /**
   * Chosen add-ons. Part of the line's identity — see `@/lib/cart/addons`.
   * Prices here are for display; checkout re-resolves them from the database.
   */
  addons?: CartAddon[] | null;
  variant_options?: Record<string, string> | null;
  quantity: number;
  name_ar?: string;
  name_en?: string;
  price?: number;
  min_order_qty?: number;
  qty_step?: number;
  /** Effective available units (null/undefined = untracked / unlimited). */
  stock?: number | null;
  image_url?: string;
  /** LQIP for the cart drawer thumbnail; null pre-backfill rows. */
  blur_data_url?: string | null;
  product_type?: string;
  /** Tier pricing snapshot — drives the qty-aware unit price in the cart. */
  has_price_tiers?: boolean;
  price_tiers?: PriceTierDTO[] | null;
  /**
   * Active campaign discount as sale/original. `price` already has the sale
   * applied; this lets a matched tier stack the same discount instead of
   * overwriting it. Null when no campaign is running.
   */
  sale_ratio?: number | null;
  /** The viewer is in a price group, so a tier may only lower `price`. */
  in_price_group?: boolean;
  /**
   * The selling unit this line was added in — `product_units.id`. Part of the
   * line's identity (see `cartItemKey`). `quantity` above is ALWAYS in base
   * units; these three fields are only the lens used to display it.
   */
  product_unit_id?: string | null;
  unit_name_ar?: string;
  unit_name_en?: string | null;
  unit_factor?: number;
};
type CartContextValue = {
  items: CartItem[];
  /** Sum of every line's quantity — units, not lines. */
  totalCount: number;
  /**
   * Number of distinct lines. This is what the badges show: "4 items" means
   * four things in the cart, however many units of each. The cart drawer has
   * always counted this way, so the badge agreeing with it is the point.
   */
  lineCount: number;
  /**
   * False during SSR and first paint, true once the cart's initial read has
   * settled. Branch on this before `items.length === 0` — the server has no
   * access to the cart, so an un-hydrated cart is indistinguishable from an
   * empty one and rendering the empty state makes content pop in later.
   */
  hydrated: boolean;
  /**
   * Add `qty` to the line. `stock` (live availability from the caller, e.g. the
   * selected variant on the PDP) caps the merged quantity; throws CartStockError
   * when the line is already at the available limit.
   */
  addItem: (productId: string, qty: number, minQty: number, variantId?: string | null, stock?: number | null, addons?: CartAddon[] | null, productUnitId?: string | null) => Promise<void>;
  updateQty: (productId: string, qty: number, minQty?: number, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => Promise<void>;
  removeItem: (productId: string, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => Promise<void>;
  clearCart: () => Promise<void>;
};
type CartDrawerContextValue = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
};
const CartContext = createContext<CartContextValue | null>(null);
const CartDrawerContext = createContext<CartDrawerContextValue | null>(null);
export function CartProvider({
  children,
  userId,
  currencyCode = DEFAULT_CURRENCY_CODE
}: {
  children: React.ReactNode;
  userId: string | null;
  /** The store's own currency: cart line prices are in it, not the display one. */
  currencyCode?: string;
}) {
  const guestCart = useGuestCart();
  const authCart = useAuthCart();
  const [isOpen, setIsOpen] = useState(false);
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  // Adds waiting to be reported. A new line has no name or price until it
  // hydrates, so the event fires from the effect below rather than from addItem.
  const pendingAddsRef = useRef<{
    key: string;
    quantity: number;
  }[]>([]);
  useEffect(() => {
    if (prevUserIdRef.current === undefined) {
      // First render: initialize
      prevUserIdRef.current = userId;
      if (userId) {
        authCart.loadItems(userId);
      }
      return;
    }
    if (!prevUserIdRef.current && userId) {
      // Guest → auth transition: merge guest cart into DB.
      //
      // The add-on selection has to travel with the line. It is part of the
      // line's identity (see `@/lib/cart/addons`), and `addAuthCartItem` keys
      // the row it merges into by `addonsKey(addons)` — so dropping it here
      // doesn't just lose the selection, it re-points the line at the plain
      // configuration of the same product and sums the guest quantity into
      // whatever plain line the account already had.
      const guestItems = guestCart.mergeAndClear();
      (async () => {
        for (const item of guestItems) {
          await authCart.addItem(userId, item.product_id, item.quantity, item.variant_id, item.addons ?? null, item.product_unit_id ?? null);
        }
        await authCart.loadItems(userId);
      })();
    } else if (userId) {
      authCart.loadItems(userId);
    }
    prevUserIdRef.current = userId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  const isAuth = userId !== null;
  const items = isAuth ? authCart.items : guestCart.items;
  const hydrated = isAuth ? authCart.hydrated : guestCart.hydrated;
  const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const lineCount = items.length;
  useEffect(() => {
    if (pendingAddsRef.current.length === 0) return;
    pendingAddsRef.current = pendingAddsRef.current.filter(({
      key,
      quantity
    }) => {
      const line = items.find(i => cartItemKey(i.product_id, i.variant_id, addonsKey(i.addons), i.product_unit_id) === key);
      if (line?.price === undefined) return true;
      trackAddToCart({
        productId: line.product_id,
        name: line.name_ar || line.name_en || line.product_id,
        price: resolveCartItemPrice(line),
        quantity,
        currency: currencyCode
      });
      return false;
    });
  }, [items, currencyCode]);
  async function addItem(productId: string, qty: number, minQty: number, variantId?: string | null, stock?: number | null, addons?: CartAddon[] | null, productUnitId?: string | null): Promise<void> {
    const key = cartItemKey(productId, variantId, addonsKey(addons), productUnitId);
    const existing = items.find(i => cartItemKey(i.product_id, i.variant_id, addonsKey(i.addons), i.product_unit_id) === key);
    // Cap the merged quantity at the available stock so repeated adds can't
    // sum past inventory. `stock` from the caller is the freshest; fall back to
    // the hydrated line's stock. The server clamps again as the source of truth.
    const max = availableUnits(stock ?? existing?.stock);
    const room = max - (existing?.quantity ?? 0);
    if (room <= 0) throw new CartStockError(max);
    const addQty = Math.min(qty, room);
    // A line sold by a selling unit (carton/stack) can only hold whole
    // multiples of that unit's factor. This function doesn't know the factor,
    // so it can't round down to the nearest whole unit — clamping to `room`
    // could leave a partial unit that checkout permanently rejects. Refuse
    // instead of silently storing it. Lines with no unit keep clamping exactly
    // as before.
    const unit = productUnitId ?? existing?.product_unit_id ?? null;
    if (unit && addQty < qty) throw new CartStockError(max);

    // Queued before the write: the state update it causes can render first.
    const pending = {
      key,
      quantity: addQty
    };
    pendingAddsRef.current.push(pending);
    try {
      if (isAuth) {
        await authCart.addItem(userId!, productId, addQty, variantId, addons, productUnitId);
        await authCart.loadItems(userId!);
      } else {
        guestCart.addItem(productId, addQty, minQty, variantId, addons, productUnitId);
      }
    } catch (err) {
      pendingAddsRef.current = pendingAddsRef.current.filter(p => p !== pending);
      throw err;
    }
  }
  async function updateQty(productId: string, qty: number, minQty = 1, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null): Promise<void> {
    const key = cartItemKey(productId, variantId, addonsKey(addons), productUnitId);
    const item = items.find(i => cartItemKey(i.product_id, i.variant_id, addonsKey(i.addons), i.product_unit_id) === key);
    const floor = item?.min_order_qty ?? minQty;
    const wanted = Math.max(qty, floor);
    const max = availableUnits(item?.stock);
    const capped = Math.min(wanted, max);
    // Same reasoning as addItem: a unit line clamped down by stock can land on
    // a partial unit that checkout permanently rejects. Refuse rather than
    // silently storing it; non-unit lines clamp exactly as before.
    const unit = productUnitId ?? item?.product_unit_id ?? null;
    if (unit && capped < wanted) throw new CartStockError(max);
    if (isAuth) {
      await authCart.updateQty(userId!, productId, capped, floor, variantId, addons, productUnitId);
    } else {
      guestCart.updateQty(productId, capped, floor, variantId, addons, productUnitId);
    }
  }
  async function removeItem(productId: string, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null): Promise<void> {
    if (isAuth) {
      await authCart.removeItem(userId!, productId, variantId, addons, productUnitId);
    } else {
      guestCart.removeItem(productId, variantId, addons, productUnitId);
    }
  }
  async function clearCart(): Promise<void> {
    if (isAuth) {
      await authCart.clearCart(userId!);
    } else {
      guestCart.clearCart();
    }
  }
  const drawerValue: CartDrawerContextValue = {
    isOpen,
    openDrawer: () => setIsOpen(true),
    closeDrawer: () => setIsOpen(false)
  };
  return <CartContext.Provider value={{
    items,
    totalCount,
    lineCount,
    hydrated,
    addItem,
    updateQty,
    removeItem,
    clearCart
  }}>
      <CartDrawerContext.Provider value={drawerValue}>
        {children}
      </CartDrawerContext.Provider>
    </CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return ctx;
}
export function useCartDrawer() {
  const ctx = useContext(CartDrawerContext);
  if (!ctx) {
    throw new Error("useCartDrawer must be used inside CartProvider");
  }
  return ctx;
}